import { NativeModules, Platform } from "react-native";
import RNFS from "react-native-fs";

/**
 * KYC-scoped face capture wrapper.
 *
 * This is intentionally SEPARATE from utils/nativeFaceCapture.ts (the
 * backend-driven login/deliveries face capture). That flow's `launch()` captures
 * AND uploads the photo to the face-capture pipeline, returning only a status —
 * JS never sees the image. The KYC user_photo, by contrast, must go through the
 * existing KYC upload (/cpdb/upload-private-image → get-private-file URL stored
 * as the user_photo kyc_item), so JS needs the captured image back.
 *
 * It therefore uses a DISTINCT native method — `captureForResult` — that only
 * captures a face-gated still and returns its file URI (no upload). The point is
 * to guarantee a real, in-frame face in every KYC selfie via the same on-device
 * face detection the modal uses, then hand the image to the unchanged KYC upload.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * NATIVE CONTRACT (implemented on FaceCaptureModule / FaceCaptureActivity):
 *
 *   captureForResult(config: {
 *     text: string;              // guidance shown on the capture screen
 *     canClose: boolean;        // allow the user to back out
 *     captureMode: "auto"|"manual";
 *     blurMinScore: number;      // -1 native default, 0 off, >0 threshold
 *     maxHeadAngle: number;      // -1 native default, 0 off, >0 max yaw/pitch/roll
 *   }): Promise<{ status: "captured" | "closed"; uri?: string }>
 *
 *   - Delegates to the same launch path; because the config carries NO
 *     token/baseUrl, the activity runs capture-only (isUploadable = false): it
 *     runs the SAME face detection/quality gate, then returns the still's file://
 *     URI instead of uploading, and never touches Redux/faceCaptureVisible.
 *   - status "captured" + uri once a valid face still is captured (that is what
 *     "ensures a face in every user photo"); "closed" when the user backs out;
 *     rejects (E_FACE_CAPTURE / E_NO_ACTIVITY) on hard errors.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * On a binary WITHOUT this method (older APK), `isKycFaceCaptureAvailable()` is
 * false and the KYC screen falls back to the existing camera picker, so those
 * builds are unaffected. Requires a native rebuild to take effect.
 */

export interface KycFaceCaptureConfig {
  text: string;
  canClose: boolean;
  captureMode: "auto" | "manual";
  blurMinScore: number;
  maxHeadAngle: number;
}

/**
 * Native result. `status: "captured"` carries a file:// `uri` for JS to upload;
 * "closed" means the user backed out; other statuses / a missing uri are treated
 * as "no photo".
 */
export interface KycFaceCaptureResult {
  status: string;
  uri?: string;
}

interface KycFaceCaptureNativeModule {
  captureForResult(config: KycFaceCaptureConfig): Promise<KycFaceCaptureResult>;
}

const nativeModule: KycFaceCaptureNativeModule | undefined =
  NativeModules?.FaceCaptureModule as KycFaceCaptureNativeModule | undefined;

export const KYC_FACE_CAPTURE_DEFAULTS: KycFaceCaptureConfig = {
  text: "अपना चेहरा फ्रेम में रखें",
  canClose: true,
  captureMode: "auto",
  blurMinScore: -1,
  maxHeadAngle: -1,
};

/**
 * True only on a binary whose FaceCaptureModule exposes capture-and-return.
 * When false, callers must use the existing camera picker.
 */
export const isKycFaceCaptureAvailable = (): boolean =>
  Platform.OS === "android" &&
  typeof nativeModule?.captureForResult === "function";

export const captureKycFace = (
  config: KycFaceCaptureConfig = KYC_FACE_CAPTURE_DEFAULTS,
): Promise<KycFaceCaptureResult> => {
  if (!nativeModule?.captureForResult) {
    return Promise.reject(new Error("captureForResult unavailable"));
  }
  return nativeModule.captureForResult(config);
};

/** A usable face still was captured (status "captured" with a file uri). */
export const isCapturedFace = (
  result: KycFaceCaptureResult | null | undefined,
): boolean => !!result && result.status === "captured" && !!result.uri;

/**
 * Delete a captured selfie's local file once it is no longer previewed (on
 * replace, remove or unmount). The native side already cleans up the raw frame,
 * retakes, rejects and cancels — the only survivor is the still it hands to JS,
 * which JS must upload (the tile previews the LOCAL file because the uploaded
 * copy is a private URL that cannot render directly), so it can only be cleared
 * after the preview is gone. Scoped to our capture-only cache dir so it never
 * touches image-picker temp files (the camera fallback path).
 */
export const deleteCapturedFace = (uri?: string | null): void => {
  if (!uri || typeof uri !== "string" || !uri.includes("/face-capture/"))
    return;
  RNFS.unlink(uri.replace("file://", "")).catch(() => {});
};
